import {useMemo, type FocusEventHandler, type Key} from 'react';
import {
    Autocomplete,
    Checkbox,
    Chip,
    TextField,
    ListItemText,
} from '@mui/material';
import type {SxProps, Theme} from '@mui/material/styles';
import {alpha, useTheme} from '@mui/material/styles';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

/** One selectable row in MultiSelectField */
export type MultiSelectOption<T extends string | number = number> = {
    value: T;
    label: string;
};

export type MultiSelectFieldProps<T extends string | number = number> = {
    options: MultiSelectOption<T>[];
    value: T[];
    onChange: (values: T[]) => void;
    /** Fired when Autocomplete input loses focus */
    onBlur?: FocusEventHandler;
    disabled?: boolean;
    label?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    placeholder?: string;
    fullWidth?: boolean;
    /** Match InputField compact (default true) */
    compact?: boolean;
    sx?: SxProps<Theme>;
};

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small"/>;
const checkedIcon = <CheckBoxIcon fontSize="small"/>;

/**
 * Multi-select Autocomplete with checkbox options; popup stays open while toggling.
 * Height / background aligned with InputField compact outlined style.
 */
export function MultiSelectField<T extends string | number = number>({
    options,
    value,
    onChange,
    onBlur,
    disabled = false,
    label,
    required,
    error,
    helperText,
    placeholder = 'Select',
    fullWidth = true,
    compact = true,
    sx,
}: MultiSelectFieldProps<T>) {
    const theme = useTheme();
    const body2Size = (theme.typography as {body2?: {fontSize?: string | number}}).body2?.fontSize;

    const optionByValue = useMemo(() => {
        const map = new Map<T, MultiSelectOption<T>>();
        for (const option of options) {
            map.set(option.value, option);
        }
        return map;
    }, [options]);

    // 1. resolve selected rows (fallback label when option missing)
    const selected = value.map(
        (v) => optionByValue.get(v) || {value: v, label: String(v)},
    );
    const empty = selected.length === 0;

    // WHY: mirror InputField outlined + compact select metrics / primary tint
    const fieldSx = {
        minWidth: 110,
        ...(compact ? {lineHeight: 1.2} : {}),
        '& .MuiOutlinedInput-root': {
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            ...(compact
                ? {
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    minHeight: 0,
                    paddingTop: '3px',
                    paddingBottom: '3px',
                    paddingLeft: '6px',
                    paddingRight: '32px',
                }
                : {}),
        },
        '& .MuiAutocomplete-input': {
            minWidth: empty ? '4.5em !important' : '0 !important',
            width: empty ? 'auto' : 0,
            flexGrow: empty ? 1 : 0,
        },
        '& .MuiInputBase-input': {
            fontSize: body2Size || undefined,
            lineHeight: 1.2,
            paddingTop: '4px !important',
            paddingBottom: '4px !important',
            height: 'unset',
        },
        '& .MuiChip-root': {
            height: 22,
            maxWidth: '100%',
            fontSize: 12,
            '& .MuiChip-label': {
                px: 0.75,
                lineHeight: 1.2,
            },
        },
    } as SxProps<Theme>;

    return (
        <Autocomplete
            multiple
            size={compact ? 'small' : 'medium'}
            disabled={disabled}
            options={options}
            value={selected}
            disableCloseOnSelect
            getOptionLabel={(option) => option.label || String(option.value)}
            isOptionEqualToValue={(left, right) => left.value === right.value}
            onBlur={onBlur}
            onChange={(_event, next) => {
                onChange(next.map((option) => option.value));
            }}
            slotProps={{
                listbox: {
                    sx: {
                        // WHY: match InputField MenuItem dense row height
                        py: 0.25,
                        '& .MuiAutocomplete-option': {
                            minHeight: 28,
                            py: 0.25,
                            px: 1,
                            alignItems: 'center',
                        },
                    },
                },
            }}
            renderOption={(props, option, {selected: isSelected}) => {
                const {key, ...optionProps} = props as typeof props & {key?: Key};
                return (
                    <li key={key} {...optionProps}>
                        <Checkbox
                            icon={uncheckedIcon}
                            checkedIcon={checkedIcon}
                            sx={{mr: 0.75, p: 0.25}}
                            checked={isSelected}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                        />
                        <ListItemText
                            primary={option.label || String(option.value)}
                            sx={{my: 0}}
                            primaryTypographyProps={{
                                fontSize: body2Size || '0.875rem',
                                lineHeight: 1.2,
                            }}
                        />
                    </li>
                );
            }}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const {key, ...tagProps} = getTagProps({index});
                    return (
                        <Chip
                            key={key}
                            label={option.label || String(option.value)}
                            size="small"
                            {...tagProps}
                            onDelete={disabled ? undefined : tagProps.onDelete}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    size={compact ? 'small' : 'medium'}
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    placeholder={empty ? placeholder : undefined}
                    fullWidth={fullWidth}
                    sx={fieldSx}
                />
            )}
            sx={[
                {width: '100%', maxWidth: '100%', minWidth: 0},
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        />
    );
}
